/* ========================================================================
   JVAN LAZY JUDGE.ME FEATURED REVIEWS (2026-07-29)
   Purpose: turn Judge.me's Shopify metafield markup into a native scroll-snap
   review rail only when the section approaches the viewport.
   Performance: no API request, public token, or third-party carousel library.
   ======================================================================== */

(() => {
  if (customElements.get('jvan-featured-reviews')) return;

  class JvanFeaturedReviews extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      const configNode = this.querySelector('[data-jvan-reviews-config]');

      try {
        this.config = JSON.parse(configNode?.textContent || '{}');
      } catch (error) {
        this.handleError('The reviews configuration could not be read.', error);
        return;
      }

      this.source = this.querySelector('[data-jvan-reviews-source]');
      this.track = this.querySelector('[data-jvan-reviews-track]');
      this.summary = this.querySelector('[data-jvan-reviews-summary]');
      this.previousButton = this.querySelector('[data-jvan-reviews-previous]');
      this.nextButton = this.querySelector('[data-jvan-reviews-next]');

      if (!this.source?.querySelector('.jdgm-carousel-item')) {
        this.handleError('No Judge.me featured reviews are available yet.');
        return;
      }

      this.bindControls();
      this.observe();
    }

    observe() {
      if (!('IntersectionObserver' in window)) {
        this.activate();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.activate();
        },
        { rootMargin: '320px 0px' }
      );

      this.observer.observe(this);
    }

    bindControls() {
      this.previousButton?.addEventListener('click', () => this.scroll(-1));
      this.nextButton?.addEventListener('click', () => this.scroll(1));
      this.track?.addEventListener('scroll', () => this.updateControls(), { passive: true });
      window.addEventListener('resize', () => this.updateControls(), { passive: true });
    }

    activate() {
      if (this.dataset.loaded === 'true') return;

      const items = Array.from(this.source.querySelectorAll('.jdgm-carousel-item'));
      this.track.replaceChildren();

      items.slice(0, Number(this.config.maxReviews) || 12).forEach((item) => {
        item.classList.add('jvan-featured-reviews__card');
        item.removeAttribute('style');

        item.querySelectorAll('[style]').forEach((node) => {
          node.style.removeProperty('width');
          node.style.removeProperty('height');
          node.style.removeProperty('display');
        });

        item.querySelectorAll('img').forEach((image) => {
          const source = image.dataset.src || image.getAttribute('data-src-retina');
          if (!image.getAttribute('src') && source) image.setAttribute('src', source);
          image.loading = 'lazy';
          image.decoding = 'async';
        });

        this.track.appendChild(item);
      });

      this.renderSummary();
      this.dataset.loaded = 'true';
      this.source.remove();
      requestAnimationFrame(() => this.updateControls());
    }

    renderSummary() {
      if (!this.summary) return;

      const rating = this.readNumber(this.config.rating);
      const count = this.readNumber(this.config.count);
      const summaryParts = [];

      if (rating) summaryParts.push(`${rating.toFixed(1)} / 5`);
      if (count) summaryParts.push(`${Math.round(count)} reviews`);

      this.summary.textContent = summaryParts.join(' · ');
      this.summary.hidden = summaryParts.length === 0;
    }

    readNumber(value) {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (value && typeof value === 'object') {
        return this.readNumber(value.value ?? value.rating ?? value.count);
      }

      const match = String(value || '').replace(/,/g, '').match(/\d+(?:\.\d+)?/);
      return match ? Number(match[0]) : 0;
    }

    scroll(direction) {
      if (!this.track) return;
      this.track.scrollBy({ left: this.track.clientWidth * 0.86 * direction, behavior: 'smooth' });
    }

    updateControls() {
      if (!this.track || !this.previousButton || !this.nextButton) return;

      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.previousButton.disabled = this.track.scrollLeft <= 4;
      this.nextButton.disabled = this.track.scrollLeft >= maximum - 4;

      const canScroll = maximum > 8;
      this.previousButton.hidden = !canScroll;
      this.nextButton.hidden = !canScroll;
    }

    handleError(message, error) {
      if (this.config?.designMode) {
        const notice = this.querySelector('[data-jvan-reviews-notice]');
        if (notice) {
          notice.textContent = message;
          notice.hidden = false;
        }
        this.track?.replaceChildren();
      } else {
        this.hidden = true;
      }

      if (error) console.warn('[JVAN featured reviews]', error);
    }
  }

  customElements.define('jvan-featured-reviews', JvanFeaturedReviews);
})();
